/* <n-before-after>: drag-to-compare slider. Pointer drag anywhere on the frame,
   keyboard through the native range input. A one-time "peek" hint plays when it first
   scrolls into view (skipped for reduced motion). */
if (!customElements.get('n-before-after')) {
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  class NBeforeAfter extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.frame = this.querySelector('.n-ba__frame');
      this.input = this.querySelector('.n-ba__range');
      this.beforeLabel = this.querySelector('.n-ba__tag--before')?.textContent.trim() || 'Before';
      if (!this.frame || !this.input) return;

      this.input.addEventListener('input', () => this.set(Number(this.input.value)));
      this.frame.addEventListener('pointerdown', (e) => this.down(e));
      this.frame.addEventListener('pointermove', (e) => this.move(e));
      ['pointerup', 'pointercancel'].forEach((t) => this.frame.addEventListener(t, (e) => this.up(e)));
      // Touch pointers are implicitly captured by the inner image; moving capture to the frame fires a
      // bubbling lostpointercapture from that child, which must not end the drag.
      this.frame.addEventListener('lostpointercapture', (e) => { if (e.target === this.frame) this.up(e); });
      this.classList.add('is-ready');

      if (!reduced() && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver(([en]) => {
          if (!en.isIntersecting) return;
          io.disconnect();
          this.peek();
        }, { threshold: 0.6 });
        io.observe(this);
      }
    }

    set(v, fromUser = true) {
      const pos = Math.max(0, Math.min(100, v));
      this.style.setProperty('--pos', `${pos}%`);
      this.input.value = String(Math.round(pos));
      this.input.setAttribute('aria-valuetext', `${Math.round(pos)}% ${this.beforeLabel}`);
      if (fromUser) this.touched = true;
    }

    fromEvent(e) {
      const r = this.frame.getBoundingClientRect();
      return ((e.clientX - r.left) / r.width) * 100;
    }

    down(e) {
      if (e.button > 0) return;
      this.drag = { id: e.pointerId, x: e.clientX, y: e.clientY, active: e.pointerType !== 'touch' };
      if (this.drag.active) {
        this.frame.setPointerCapture?.(e.pointerId);
        this.classList.add('is-dragging');
        this.set(this.fromEvent(e));
      }
    }

    move(e) {
      if (!this.drag || e.pointerId !== this.drag.id) return;
      if (!this.drag.active) {
        // Touch: only take over once the gesture is clearly horizontal (vertical scroll stays native).
        const dx = Math.abs(e.clientX - this.drag.x);
        const dy = Math.abs(e.clientY - this.drag.y);
        if (dx < 6 || dx < dy) return;
        this.drag.active = true;
        this.frame.setPointerCapture?.(e.pointerId);
        this.classList.add('is-dragging');
      }
      this.set(this.fromEvent(e));
    }

    up(e) {
      if (!this.drag) return;
      if (e.type === 'pointerup' && !this.drag.active) this.set(this.fromEvent(e)); // a tap jumps there
      this.drag = null;
      this.classList.remove('is-dragging');
    }

    peek() {
      const start = Number(this.input.value);
      const keys = [start, start + 14, start - 12, start];
      const dur = 1400;
      const t0 = performance.now();
      const step = (now) => {
        if (this.touched) return;
        const p = Math.min(1, (now - t0) / dur);
        const seg = Math.min(keys.length - 2, Math.floor(p * (keys.length - 1)));
        const local = p * (keys.length - 1) - seg;
        const ease = 0.5 - Math.cos(local * Math.PI) / 2;
        this.set(keys[seg] + (keys[seg + 1] - keys[seg]) * ease, false);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }
  customElements.define('n-before-after', NBeforeAfter);
}
